import { CountryRow, ScenarioId } from '../types/census';
import { formatCompact, formatPct } from './format';
import { agentsPerCapitaFor } from './estimates';

import { builderCounts, modelCounts } from './ecosystem';

export type MetricId = 'population' | 'internet' | 'smartphone' | 'agents' | 'median' | 'builders' | 'models';
export type ModeKey = 'countries' | 'internet' | 'mobile' | 'median' | 'curated';

export interface MetricDef {
  id: MetricId;
  label: string;
  scale: 'log' | 'linear';
  get: (r: CountryRow) => number | null;
  format: (v: number) => string;
  sourceId: string;
  modeKey: ModeKey;
  sourceLabel: string;
  estimate?: boolean;
}

export const mapMetrics: MetricDef[] = [
  { id: 'population', label: 'Population', scale: 'log', get: (r) => (r.population > 0 ? r.population : null), format: (v) => formatCompact(v), sourceId: 'restcountries', modeKey: 'countries', sourceLabel: 'REST Countries' },
  { id: 'internet', label: 'Internet users (%)', scale: 'linear', get: (r) => r.internetPct?.value ?? null, format: (v) => formatPct(v), sourceId: 'wbInternet', modeKey: 'internet', sourceLabel: 'World Bank' },
  { id: 'smartphone', label: 'Smartphone penetration', scale: 'linear', get: (r) => r.smartphonePct, format: (v) => `${formatPct(v)} (est.)`, sourceId: 'gsma', modeKey: 'mobile', sourceLabel: 'World Bank × GSMA', estimate: true },
  { id: 'agents', label: 'Agent density', scale: 'linear', get: (r) => r.agentsPerCapita, format: (v) => `${v.toFixed(2)} / person (est.)`, sourceId: 'censusModel', modeKey: 'mobile', sourceLabel: 'Census model', estimate: true },
  { id: 'median', label: 'Median age', scale: 'linear', get: (r) => r.medianAge, format: (v) => `${v.toFixed(1)} yrs`, sourceId: 'unwpp', modeKey: 'median', sourceLabel: 'UN WPP 2024' },
  { id: 'builders', label: 'AI builders', scale: 'log', get: (r) => builderCounts[r.cca3] ?? 0, format: (v) => `${Math.round(v)} orgs`, sourceId: 'censusCurated', modeKey: 'curated', sourceLabel: 'Builders dataset' },
  { id: 'models', label: 'Models released', scale: 'log', get: (r) => modelCounts[r.cca3] ?? 0, format: (v) => `${Math.round(v)} models`, sourceId: 'censusCurated', modeKey: 'curated', sourceLabel: 'Curated model table' },
];

export function metricsForScenario(scenario: ScenarioId): MetricDef[] {
  return mapMetrics.map((m) =>
    m.id === 'agents'
      ? { ...m, get: (r: CountryRow) => agentsPerCapitaFor(r, scenario), format: (v: number) => `${v < 0.1 ? v.toFixed(4) : v.toFixed(2)} / person (est.)` }
      : m,
  );
}

export function metricDomain(metric: MetricDef, rows: CountryRow[]): [number, number] {
  const vals = rows.map(metric.get).filter((v): v is number => v != null && (metric.scale === 'linear' || v > 0));
  if (vals.length === 0) return [0, 1];
  return [Math.min(...vals), Math.max(...vals)];
}

export function normalize(metric: MetricDef, v: number, [min, max]: [number, number]): number {
  if (metric.scale === 'log') {
    const lmin = Math.log10(Math.max(1, min));
    const lmax = Math.log10(Math.max(1, max));
    return lmax === lmin ? 0.5 : (Math.log10(Math.max(1, v)) - lmin) / (lmax - lmin);
  }
  return max === min ? 0.5 : (v - min) / (max - min);
}
